import React, { useContext, useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { IRole, useGetRolesQuery, useUpdateUserDetailsMutation } from '../../../generated/graphql';
import { Colors } from '../../../utils/theme';
import { PeopleAlt, Person } from '@mui/icons-material';
import { getLabelColor, getRequiredFieldLabelColor } from '../../../utils/styleUtils';
import { coloring } from './UsersList';
import { ErrorTexts } from './CreateUser';
import { sameUsernameExists, updateUserGroupsInCache } from '../../../utils/clientUtils';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Roles } from '../../../enums/RoleEnums';
import EditButton from '../../Common/Buttons/EditButton';
import InputComponent from '../../Common/Fields/InputComponent';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';

type IErrors = {
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  addUserErrors?: (string | null)[] | null | undefined;
};

type IUserDetails = {
  username: string;
  firstName: string;
  lastName: string;
};

const defaultErrorObj: IErrors = {
  username: null,
  firstName: null,
  lastName: null
};

const UserGeneralTab: React.FC = () => {
  const { dispatch, state } = useContext(AppContext);
  const [editable, setEditable] = useState(false);
  const [formErrors, setFormErrors] = useState(defaultErrorObj);
  const [userDetails, setUserDetails] = useState<IUserDetails>({
    username: state.selectedUser?.username || '',
    firstName: state.selectedUser?.first_name || '',
    lastName: state.selectedUser?.last_name || ''
  });
  const [userGroups, setUserGroups] = useState<IRole[]>([]);
  const { data } = useGetRolesQuery();
  const [updateUser] = useUpdateUserDetailsMutation();

  useEffect(() => {
    if (data && data.result && state.selectedUser && state.selectedUser.roles.length > 0) {
      setUserGroups(
        (data?.result as IRole[]).filter((r) => state.selectedUser?.roles.includes(r?.name))
      );
    }
  }, [data]);

  const handleCancelEdit = () => {
    setEditable(false);
    setFormErrors(defaultErrorObj);
    setUserDetails({
      username: state.selectedUser?.username || '',
      firstName: state.selectedUser?.first_name || '',
      lastName: state.selectedUser?.last_name || ''
    });
    setUserGroups(
      (data?.result as IRole[]).filter((r) => state.selectedUser?.roles.includes(r?.name))
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    const trimedValue = value.trim();
    if (trimedValue && trimedValue.length > 1) {
      setFormErrors({ ...formErrors, [name]: null });
    } else {
      // eslint-disable-next-line
      // @ts-ignore
      setFormErrors({ ...formErrors, [name]: ErrorTexts[name] });
    }
    setUserDetails({ ...userDetails, [name]: value });
  };

  const handleUserGroupsChange = (_: unknown, newValue: IRole[]) => {
    setUserGroups(newValue);
  };

  const handleSaveEdit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      username: null,
      firstName: null,
      lastName: null
    };

    if (userDetails.username.length < 2) {
      hasErrors = true;
      errors.username = ErrorTexts.username;
    }
    if (userDetails.firstName.length < 2) {
      hasErrors = true;
      errors.firstName = ErrorTexts.firstName;
    }
    if (userDetails.lastName.length < 2) {
      hasErrors = true;
      errors.lastName = ErrorTexts.lastName;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const usernameExists = sameUsernameExists(userDetails.username, state?.selectedUser?.id);
      if (usernameExists) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: `Username ${userDetails.username} already exists`
          }
        });
      } else {
        updateUser({
          variables: {
            id: parseInt(state?.selectedUser?.id as string),
            input: {
              username: userDetails.username,
              first_name: userDetails.firstName,
              last_name: userDetails.lastName,
              user_role_ids: userGroups.map((ug) => parseInt(ug.id))
            }
          },
          update(_, { data }) {
            if (data && data?.result) {
              updateUserGroupsInCache(data.result);
              dispatch({
                type: Actions.SetSelectedUser,
                payload: {
                  user: data.result
                }
              });
              setEditable(false);
              dispatch({
                type: Actions.ShowMessage,
                payload: {
                  severity: 'success',
                  text: `Successfully updated details for ${data.result.username}`
                }
              });
            }
          }
        }).catch((e) => console.log(e.message));
      }
    }
  };
  const getOptionLabel = (option: IRole): string => {
    if (option && option.name) {
      return option.name.replace('_', ' ') as string;
    } else {
      return '';
    }
  };

  return (
    <Box id="user-details">
      <Box sx={{ textAlign: 'right', width: '100%', paddingTop: '40px', paddingRight: '35px' }}>
        <EditButton
          id="user-details-edit-button"
          onClick={() => setEditable(true)}
          disabled={editable}
          editButtonColor={editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          paddingTop: '30px',
          paddingLeft: '25%',
          paddingRight: '25%',
          gap: '20px 80px'
        }}
      >
        <InputComponent
          id="username"
          label="Username"
          value={userDetails.username}
          onChange={handleInputChange}
          errorMsg={formErrors.username}
          disabled={!editable}
          additionalStyle={{ gridColumn: 'span 2', marginTop: '30px' }}
          startAdornment={
            <Person
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(editable, formErrors.username)
              }}
            />
          }
        />
        <InputComponent
          id="firstName"
          label="First Name"
          value={userDetails.firstName}
          onChange={handleInputChange}
          errorMsg={formErrors.firstName}
          disabled={!editable}
          additionalStyle={{ gridColumn: 'span 1', marginTop: '30px' }}
          startAdornment={
            <Person
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(editable, formErrors.firstName)
              }}
            />
          }
        />
        <InputComponent
          id="lastName"
          label="Last Name"
          value={userDetails.lastName}
          onChange={handleInputChange}
          errorMsg={formErrors.lastName}
          disabled={!editable}
          additionalStyle={{ gridColumn: 'span 1', marginTop: '30px' }}
          startAdornment={
            <Person
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(editable, formErrors.lastName)
              }}
            />
          }
        />
        <AutocompleteComponent
          id="user-details-assign-user-group-autocomplete"
          label="User Groups"
          values={userGroups}
          options={
            (data?.result.filter(
              (r) => r?.name !== Roles.Admin && r?.name !== Roles.InvoiceEditor
            ) as never[]) || []
          }
          isSingle={false}
          onChange={handleUserGroupsChange}
          disabled={!editable}
          getOptionLabel={(option: IRole) => getOptionLabel(option)}
          additionalStyle={{ gridColumn: 'span 2', marginTop: '30px' }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                sx={{
                  backgroundColor: Colors.white,
                  color: coloring(option.name),
                  '& .MuiChip-deleteIcon': {
                    width: '20px',
                    height: '20px',
                    color: Colors.skyBlue,
                    '&:hover': {
                      color: Colors.main
                    }
                  }
                }}
                variant="outlined"
                label={`${option.name.replace('_', ' ')}`}
                {...getTagProps({ index })}
              />
            ))
          }
          startAdornment={
            <PeopleAlt
              style={{
                marginRight: '12px',
                color: getLabelColor(editable)
              }}
            />
          }
        />
        {editable && (
          <Box className="centralize" sx={{ gridColumn: 'span 2', marginTop: '190px' }}>
            <CancelButton id="cancel-edit-user-button" onClick={handleCancelEdit} text={'Cancel'} />
            <SaveButton id="save-edit-user-button" onClick={handleSaveEdit} text={'Save'} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserGeneralTab;
